import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  acceptInvite,
  createEditProposal,
  createMap as createMapApi,
  deleteMap as deleteMapApi,
  getComments,
  getFeatureHistory,
  getJson,
  getMapSettings,
  getNotifications,
  getProposalChanges,
  getProposalDetail,
  getProposals,
  getProposedFeatures,
  getRevisions,
  getTypeCatalog,
  postJson,
  putMapSettings,
  reindexMap,
  renameMap,
  updateEditProposal,
  uploadRaw,
  verifyChain,
  type AppNotification,
  type AuthOptions,
  type ChangeItem,
  type Comment,
  type Decision,
  type EditOperation,
  type Feature,
  type FeatureCollection,
  type FeatureHistoryEntry,
  type ImportProposalResult,
  type MapSettings,
  type MapSummary,
  type Me,
  type Proposal,
  type ProposalDetail,
  type Revision,
  type TypeCatalogEntry,
  type Warning,
  type WarningSeverity,
} from './api';
import { mergeEditedFeatures } from './edit';
import { newFeatureId } from './id';
import { makeT, type Lang, type Translate } from './i18n';
import { AdminPage } from './AdminPage';
import { Logo } from './Logo';
import { TeamPage } from './TeamPage';
import { tenantLabel } from './tenant';
import { MapView, type LayerKey } from './MapView';
import { NewMapDialog } from './NewMapDialog';
import { NotificationsMenu } from './NotificationsMenu';
import { TypeField } from './TypeField';
import { ActionRow, ClearIcon, Field, FieldGrid, IconButton, Section } from './ui';
import { APP_VERSION } from './version';
import './styles.css';

const REVIEWER_ROLES = ['tenant_admin', 'contributor'];
const CONTRIBUTOR_ROLES = ['tenant_admin', 'contributor'];

function nameOf(change: ChangeItem, missing = '(no name)'): string {
  const name = (change.after ?? change.before)?.properties?.name;
  return typeof name === 'string' && name.trim() ? name : missing;
}

/** The feature this change is about (the incoming one, except for deletions). */
function subjectOf(change: ChangeItem) {
  return change.action === 'delete' ? change.before : (change.after ?? change.before);
}

function isUnnamed(change: ChangeItem): boolean {
  const name = subjectOf(change)?.properties?.name;
  return !(typeof name === 'string' && name.trim());
}

/** Mirrors core's exact comparison key (NFC and outer trim only). */
function comparisonKey(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const v = value.normalize('NFC').trim();
  return v.length > 0 ? v : undefined;
}

/** Mirrors core's identity key so we can point out the ambiguous candidates. */
function identityKeyOf(feature: Feature, strategy: string[]): string | null {
  const p = feature.properties ?? {};
  const parts: string[] = [];
  for (const field of strategy) {
    let value: string | undefined;
    if (field === 'id') value = feature.id;
    else if (field === 'name') value = comparisonKey(p.name);
    else if (field === 'description') value = comparisonKey(p.description);
    else value = comparisonKey(p.type);
    if (value === undefined) return null;
    parts.push(value);
  }
  return parts.join('\u0000');
}

/** Existing master features that make a `two-candidates` row ambiguous. */
function candidatesFor(
  change: ChangeItem | undefined,
  data: FeatureCollection | null,
  settings: MapSettings | null,
): Feature[] {
  if (!change || change.ambiguity !== 'two-candidates' || !data?.features || !change.after) return [];
  const strategy = settings?.identityStrategy ?? ['name', 'type'];
  const source: Feature = {
    type: 'Feature',
    id: change.featureId ?? '',
    geometry: (change.after.geometry ?? { type: 'Point', coordinates: [] }) as Feature['geometry'],
    properties: change.after.properties ?? {},
  };
  const key = identityKeyOf(source, strategy);
  if (!key) return [];
  return data.features.filter((f) => identityKeyOf(f, strategy) === key);
}

/** Include the candidates in the focus bounds so they are not off-screen. */
function focusGeometryFor(change: ChangeItem | undefined, candidates: Feature[]): unknown {
  const geometry = (change?.after ?? change?.before)?.geometry;
  if (!geometry) return undefined;
  if (candidates.length === 0) return geometry;
  return {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: {}, geometry },
      ...candidates.map((f) => ({ type: 'Feature', properties: {}, geometry: f.geometry })),
    ],
  };
}

/** Control metadata for a feature created in-app (schema v1). */
function newWaystoneMeta(kind: 'waypoint' | 'track' | 'area' = 'waypoint') {
  return {
    schema: 1,
    kind,
    status: 'active',
    created_revision: 0,
    last_modified_revision: 0,
    photos: [],
    extra: {},
  };
}

/** The draggable vertices of a line or polygon (the ring, without its closing point). */
function editableVertices(feature: Feature): Array<[number, number]> {
  const geometry = feature.geometry;
  if (geometry.type === 'LineString') return geometry.coordinates as Array<[number, number]>;
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates as Array<Array<[number, number]>>;
    const ring = rings[0] ?? [];
    if (ring.length >= 2) {
      const first = ring[0]!;
      const last = ring[ring.length - 1]!;
      if (first[0] === last[0] && first[1] === last[1]) return ring.slice(0, -1);
    }
    return ring;
  }
  return [];
}

/** Rebuild a line/polygon geometry from edited vertices (polygon rings are closed). */
function geometryWithVertices(feature: Feature, vertices: Array<[number, number]>): Feature['geometry'] {
  const geometry = feature.geometry;
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates as Array<Array<[number, number]>>;
    const closed = vertices.length > 0 ? [...vertices, vertices[0]!] : [];
    return { type: 'Polygon', coordinates: [closed, ...rings.slice(1)] };
  }
  return { type: 'LineString', coordinates: vertices };
}

function minimumVertices(feature: Feature): number {
  return feature.geometry.type === 'Polygon' ? 3 : 2;
}

function firstCoord(geometry: unknown): [number, number] | null {
  const g = geometry as { type?: string; coordinates?: unknown } | null;
  if (!g || g.coordinates === undefined) return null;
  const coords = g.coordinates as unknown;
  if (g.type === 'Point' && Array.isArray(coords)) {
    return [Number(coords[0]), Number(coords[1])];
  }
  if (Array.isArray(coords) && Array.isArray(coords[0])) {
    let first = coords[0] as unknown[];
    if (Array.isArray(first[0])) first = first[0] as unknown[];
    return [Number(first[0]), Number(first[1])];
  }
  return null;
}

function distanceMeters(a: [number, number], b: [number, number]): number {
  const R = 6371008.8;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b[1] - a[1]);
  const dLon = rad(b[0] - a[0]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function formatGeometry(geometry: unknown, t: (key: string) => string): string {
  const g = geometry as { type?: string; coordinates?: unknown } | null;
  if (!g) return '';
  if (g.type === 'Point' && Array.isArray(g.coordinates)) {
    const c = g.coordinates as number[];
    return `${Number(c[1]).toFixed(6)}, ${Number(c[0]).toFixed(6)}`;
  }
  if (g.type === 'LineString' && Array.isArray(g.coordinates)) {
    return `${(g.coordinates as unknown[]).length} ${t('change.vertices')}`;
  }
  if (g.type === 'Polygon' && Array.isArray(g.coordinates)) {
    const vertices = (g.coordinates as unknown[][]).flat().length;
    return `${vertices} ${t('change.vertices')}`;
  }
  if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
    const vertices = (g.coordinates as unknown[][][]).flat(2).length;
    return `${vertices} ${t('change.vertices')}`;
  }
  return g.type ?? '';
}

interface DiffRow {
  label: string;
  from: string;
  to: string;
  changed: boolean;
}

function propertyValues(props: Record<string, unknown> | undefined): Map<string, string> {
  const values = new Map<string, string>();
  if (!props) return values;
  const add = (label: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    values.set(label, typeof value === 'string' ? value : JSON.stringify(value));
  };
  add('name', props.name);
  add('type', props.type);
  add('description', props.description);
  add('symbol', props.symbol);
  add('elevation_m', props.elevation_m);
  add('observed_at', props.observed_at);
  const attrs = props.attrs;
  if (attrs && typeof attrs === 'object') {
    for (const [key, value] of Object.entries(attrs as Record<string, unknown>)) add(key, value);
  }
  return values;
}

/** Field-by-field "from → to" rows for one change (partner-aware for moves). */
function changeDiff(
  before: { geometry?: unknown; properties?: Record<string, unknown> } | null,
  after: { geometry?: unknown; properties?: Record<string, unknown> } | null,
  t: (key: string) => string,
): DiffRow[] {
  const from = propertyValues(before?.properties);
  const to = propertyValues(after?.properties);
  const labels = [...new Set([...from.keys(), ...to.keys()])];
  const rows: DiffRow[] = labels.map((label) => ({
    label,
    from: from.get(label) ?? '—',
    to: to.get(label) ?? '—',
    changed: (from.get(label) ?? '') !== (to.get(label) ?? ''),
  }));
  const fromCoordinate = before ? formatGeometry(before.geometry, t) : '';
  const toCoordinate = after ? formatGeometry(after.geometry, t) : '';
  if (fromCoordinate || toCoordinate) {
    rows.push({
      label: t('change.coordinates'),
      from: fromCoordinate || '—',
      to: toCoordinate || '—',
      changed: fromCoordinate !== toCoordinate,
    });
  }
  return rows;
}

function revisionText(revision: Revision): string {
  const summary = revision.summary as {
    counts?: { total?: number };
    changes?: { add?: number; update?: number; delete?: number };
    source?: string;
  } | null;
  const title = revision.message?.trim() || `#${revision.number}`;
  if (summary?.source === 'merge' && summary.changes) {
    const { add = 0, delete: removed = 0, update = 0 } = summary.changes;
    return `${title} · +${add} / -${removed} / ~${update}`;
  }
  const total = summary?.counts?.total;
  return total == null ? title : `${title} · ${total}`;
}

/** A warning is either a legacy pre-rendered string or a structured code. */
function warningText(warning: Warning | string, t: Translate): string {
  return typeof warning === 'string' ? warning : t(`warning.${warning.code}`, warning.params);
}

function warningSeverity(warning: Warning | string): WarningSeverity {
  return typeof warning === 'string' ? 'info' : warning.severity;
}

type Tab = 'map' | 'proposals' | 'history' | 'settings';

export default function App() {
  const [lang, setLang] = useState<Lang>('sv');
  const t = useMemo(() => makeT(lang), [lang]);

  const [me, setMe] = useState<Me | null>(null);
  const [authOptions, setAuthOptions] = useState<AuthOptions | null>(null);
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [selected, setSelected] = useState<MapSummary | null>(null);
  const [data, setData] = useState<FeatureCollection | null>(null);
  const [layer, setLayer] = useState<LayerKey>('satellite');
  const [type, setType] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [detail, setDetail] = useState<ProposalDetail | null>(null);
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [threadKey, setThreadKey] = useState<string | null>(null);
  const [threadText, setThreadText] = useState('');
  const [editing, setEditing] = useState(false);
  const [editBase, setEditBase] = useState<FeatureCollection | null>(null);
  const [editProposalId, setEditProposalId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Feature>>({});
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [selectedVertex, setSelectedVertex] = useState<number | null>(null);
  const [armed, setArmed] = useState<'add' | 'move' | 'addVertex' | 'drawLine' | 'drawArea' | null>(null);
  const [drawCoords, setDrawCoords] = useState<Array<[number, number]>>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const commentsByItem = useMemo(() => {
    const map = new Map<string | null, Comment[]>();
    for (const c of comments) {
      (map.get(c.itemKey) ?? map.set(c.itemKey, []).get(c.itemKey)!).push(c);
    }
    return map;
  }, [comments]);

  /** Pair a delete+add that the differ flagged as a possible move. */
  const movePartners = useMemo(() => {
    const map = new Map<string, { del?: ChangeItem; add?: ChangeItem }>();
    for (const c of changes) {
      if (!c.possibleMoveOf) continue;
      const entry = map.get(c.possibleMoveOf) ?? {};
      if (c.action === 'delete') entry.del = c;
      else if (c.action === 'add') entry.add = c;
      map.set(c.possibleMoveOf, entry);
    }
    return map;
  }, [changes]);

  /** The review list is sorted by name so a feature is easy to find (locale-aware). */
  const sortedChanges = useMemo(
    () =>
      [...changes].sort((a, b) =>
        nameOf(a, t('review.unnamed')).localeCompare(nameOf(b, t('review.unnamed')), lang, {
          sensitivity: 'base',
          numeric: true,
        }),
      ),
    [changes, lang, t],
  );

  /** Tenants the current user may create maps in (tenant admins only). */
  const adminTenants = useMemo(() => {
    const seen = new Map<string, string>();
    if (!me) return [] as Array<{ id: string; name: string }>;
    for (const m of me.memberships) {
      if (m.role === 'tenant_admin' && m.tenantStatus === 'active') seen.set(m.tenantId, m.tenantName);
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [me]);

  const baseFeatures = useMemo(() => editBase?.features ?? [], [editBase]);
  const effectiveFeatures = useMemo(
    () => mergeEditedFeatures(baseFeatures, drafts, deletedIds),
    [baseFeatures, drafts, deletedIds],
  );
  const selectedFeature = effectiveFeatures.find((f) => f.id === selectedFeatureId) ?? null;
  const isLineSelected = selectedFeature?.geometry.type === 'LineString';
  const isAreaSelected = selectedFeature?.geometry.type === 'Polygon';
  const canEditVertices = isLineSelected || isAreaSelected;
  const isDrawing = armed === 'drawLine' || armed === 'drawArea';
  const shapeVertices = useMemo<Array<[number, number]>>(
    () => (canEditVertices && selectedFeature ? editableVertices(selectedFeature) : []),
    [canEditVertices, selectedFeature],
  );
  const vertices = isDrawing ? drawCoords : shapeVertices;
  const previewLine =
    armed === 'drawArea' && drawCoords.length >= 3
      ? [...drawCoords, drawCoords[0]!]
      : isDrawing
        ? drawCoords
        : [];
  const editData = useMemo<FeatureCollection>(
    () => ({ type: 'FeatureCollection', features: effectiveFeatures }),
    [effectiveFeatures],
  );
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [typeCatalog, setTypeCatalog] = useState<TypeCatalogEntry[]>([]);
  const [tab, setTab] = useState<Tab>('map');
  const [showNotifications, setShowNotifications] = useState(false);
  const [featureInfo, setFeatureInfo] = useState<{
    id: string;
    name?: string;
    properties?: Record<string, unknown>;
    geometry?: unknown;
  } | null>(null);
  const [featureHistory, setFeatureHistory] = useState<FeatureHistoryEntry[]>([]);
  const [showNewMap, setShowNewMap] = useState(false);
  const [settings, setSettings] = useState<MapSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [mapName, setMapName] = useState('');

  const role = selected?.role ?? '';
  const canApprove = REVIEWER_ROLES.includes(role);
  const canContribute = CONTRIBUTOR_ROLES.includes(role);
  const canComment = Boolean(role);
  const canConfigure = role === 'tenant_admin';
  const canMerge = Boolean(
    detail && me && (role === 'tenant_admin' || detail.authorId === me.user.id),
  );

  const loadMe = useCallback(async (): Promise<boolean> => {
    try {
      setMe(await getJson<Me>('/api/v1/me'));
      return true;
    } catch {
      setMe(null);
      return false;
    }
  }, []);

  const loadAuthOptions = useCallback(async () => {
    try {
      setAuthOptions(await getJson<AuthOptions>('/api/v1/auth/options'));
    } catch {
      setAuthOptions(null);
    }
  }, []);

  const loadMaps = useCallback(async (preferId?: string) => {
    const res = await getJson<{ maps: MapSummary[] }>('/api/v1/maps');
    setMaps(res.maps);
    setSelected(
      (current) => res.maps.find((m) => m.id === (preferId ?? current?.id)) ?? res.maps[0] ?? null,
    );
  }, []);

  const loadFeatures = useCallback(
    async (map: MapSummary | null) => {
      if (!map) {
        setData(null);
        return;
      }
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      if (kind) params.set('kind', kind);
      if (q) params.set('q', q);
      const qs = params.toString();
      setData(
        await getJson<FeatureCollection>(
          `/api/v1/tenants/${map.tenantId}/maps/${map.id}/features${qs ? `?${qs}` : ''}`,
        ),
      );
    },
    [type, kind, q],
  );

  const loadProposals = useCallback(async (map: MapSummary | null) => {
    if (!map) {
      setProposals([]);
      return;
    }
    setProposals((await getProposals(map.tenantId, map.id)).proposals);
  }, []);

  const loadNotifications = useCallback(async (map: MapSummary | null) => {
    if (!map) {
      setNotifications([]);
      return;
    }
    setNotifications((await getNotifications(map.tenantId)).notifications);
  }, []);

  const loadRevisions = useCallback(async (map: MapSummary | null) => {
    if (!map) {
      setRevisions([]);
      return;
    }
    setRevisions((await getRevisions(map.tenantId, map.id)).revisions);
  }, []);

  const loadSettings = useCallback(async (map: MapSummary | null) => {
    if (!map) {
      setSettings(null);
      setMapName('');
      return;
    }
    setMapName(map.name);
    setSettings(await getMapSettings(map.tenantId, map.id));
  }, []);

  const loadReview = useCallback(async (map: MapSummary | null, proposalId: string | null) => {
    if (!map || !proposalId) {
      setChanges([]);
      setDetail(null);
      setComments([]);
      return;
    }
    const [ch, dt, cm] = await Promise.all([
      getProposalChanges(map.tenantId, map.id, proposalId),
      getProposalDetail(map.tenantId, map.id, proposalId),
      getComments(map.tenantId, map.id, proposalId),
    ]);
    setChanges(ch.changes);
    setDetail(dt);
    setComments(cm.comments);
  }, []);

  useEffect(() => {
    void (async () => {
      void loadAuthOptions();
      const url = new URL(window.location.href);
      const token = url.searchParams.get('token');
      const invite = url.searchParams.get('invite');
      if (token) {
        try {
          await postJson('/api/v1/auth/verify', { token });
        } catch {
          setMessage(t('login.tokenFailed'));
        }
        url.searchParams.delete('token');
      }
      if (invite) {
        try {
          await acceptInvite(invite);
          setMessage(t('login.inviteAccepted'));
        } catch {
          setMessage(t('login.inviteFailed'));
        }
        url.searchParams.delete('invite');
      }
      if (token || invite) {
        window.history.replaceState({}, '', `${url.pathname}${url.search}`);
      }
      if (await loadMe()) await loadMaps();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!me) return;
    void loadFeatures(selected);
  }, [me, selected, loadFeatures]);

  useEffect(() => {
    if (!me) {
      setTypeCatalog([]);
      return;
    }
    void (async () => {
      try {
        setTypeCatalog((await getTypeCatalog()).types);
      } catch {
        setTypeCatalog([]);
      }
    })();
  }, [me]);

  useEffect(() => {
    if (!me) return;
    void loadProposals(selected);
    void loadNotifications(selected);
    void loadRevisions(selected);
    void loadSettings(selected);
  }, [me, selected, loadProposals, loadNotifications, loadRevisions, loadSettings]);

  // Filter options come from the unfiltered master, so they never shrink as a
  // filter is applied.
  useEffect(() => {
    if (!me || !selected) {
      setAllTypes([]);
      return;
    }
    void (async () => {
      try {
        const collection = await getJson<FeatureCollection>(
          `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/features`,
        );
        const values = new Set<string>();
        for (const feature of collection.features) {
          const value = feature.properties.type;
          if (typeof value === 'string' && value) values.add(value);
        }
        setAllTypes([...values].sort());
      } catch {
        setAllTypes([]);
      }
    })();
  }, [me, selected]);

  // Transient feedback must not shift the layout; it auto-dismisses (and can be
  // closed immediately).
  useEffect(() => {
    if (!message) return;
    const id = window.setTimeout(() => setMessage(''), 8000);
    return () => window.clearTimeout(id);
  }, [message]);

  useEffect(() => {
    void loadReview(selected, reviewId);
  }, [selected, reviewId, loadReview]);

  // Opening a different proposal starts with all detail rows closed.
  useEffect(() => {
    setDetailKey(null);
  }, [reviewId]);

  // A pending "delete map?" confirmation never survives a tab/map change.
  useEffect(() => {
    setDeleteConfirm(false);
  }, [tab, selected]);

  // Scroll only when the opened row (with its details) and the row below are not
  // both visible; otherwise leave the list where the reviewer put it.
  useEffect(() => {
    if (!detailKey) return;
    const element = document.getElementById(`change-${detailKey}`);
    const panel = element?.closest('.panel');
    if (!element || !panel) return;

    const nextRow = (() => {
      const sibling = element.nextElementSibling;
      if (sibling instanceof HTMLElement) return sibling;
      let section = element.closest('section')?.nextElementSibling ?? null;
      while (section) {
        const candidate = section.querySelector('li');
        if (candidate instanceof HTMLElement) return candidate;
        section = section.nextElementSibling;
      }
      return null;
    })();

    const panelRect = panel.getBoundingClientRect();
    const rowRect = element.getBoundingClientRect();
    const nextRect = nextRow?.getBoundingClientRect() ?? null;
    const bottom = nextRect ? nextRect.bottom : rowRect.bottom;
    const fitsTogether = rowRect.height + (nextRect?.height ?? 0) <= panelRect.height;

    if (fitsTogether) {
      if (rowRect.top >= panelRect.top && bottom <= panelRect.bottom) return;
      // Minimal move: only enough to reveal the row and the next one.
      if (rowRect.top < panelRect.top) {
        panel.scrollTop += rowRect.top - panelRect.top - 8;
      } else if (bottom > panelRect.bottom) {
        panel.scrollTop += bottom - panelRect.bottom + 8;
      }
      return;
    }

    // Too tall to show both: at least start the row at the top.
    if (rowRect.top < panelRect.top || rowRect.bottom > panelRect.bottom) {
      panel.scrollTop += rowRect.top - panelRect.top - 8;
    }
  }, [detailKey]);

  const onTokenLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = String(new FormData(event.currentTarget).get('token') ?? '').trim();
    if (!token) return;
    try {
      await postJson('/api/v1/auth/verify', { token });
      setMessage('');
      if (await loadMe()) await loadMaps();
    } catch {
      setMessage(t('login.tokenFailed'));
    }
  };

  const onRequestLink = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const email = String(new FormData(form).get('email') ?? '').trim();
    if (!email) return;
    try {
      await postJson('/api/v1/auth/request-link', { email });
    } catch {
      // The endpoint is deliberately enumeration-resistant; show the same
      // neutral confirmation even if the request could not be processed.
    }
    setMessage(t('login.linkRequested'));
    form.reset();
  };

  const onDevLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email') ?? '').trim();
    try {
      await postJson('/api/v1/auth/dev-login', { email });
      setMessage('');
      if (await loadMe()) await loadMaps();
    } catch {
      setMessage(t('login.failed'));
    }
  };

  const resetEditor = () => {
    setEditing(false);
    setEditBase(null);
    setEditProposalId(null);
    setDrafts({});
    setDeletedIds([]);
    setSelectedFeatureId(null);
    setSelectedVertex(null);
    setArmed(null);
    setDrawCoords([]);
  };

  const onLogout = async () => {
    await postJson('/api/v1/auth/logout');
    setMe(null);
    setMaps([]);
    setSelected(null);
    setData(null);
    setProposals([]);
    setReviewId(null);
    setFeatureInfo(null);
    setTab('map');
    setShowNotifications(false);
    setAdminOpen(false);
    setTeamOpen(false);
    resetEditor();
  };

  const onImport = async (file: File) => {
    if (!selected) return;
    setBusy(true);
    try {
      const bytes = await file.arrayBuffer();
      const contentType =
        file.name.endsWith('.json') || file.name.endsWith('.geojson')
          ? 'application/geo+json'
          : 'application/gpx+xml';
      const result = (await uploadRaw(
        `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/imports?filename=${encodeURIComponent(file.name)}`,
        bytes,
        contentType,
      )) as ImportProposalResult;
      if (result.noChanges || !result.proposalId) {
        setMessage(t('import.noChanges'));
        return;
      }
      const c = result.summary.counts;
      setMessage(`${t('import.done')}: +${c.add} ~${c.update} -${c.delete}`);
      await loadProposals(selected);
      setTab('proposals');
      setReviewId(result.proposalId);
    } catch (err) {
      setMessage(String(err));
    } finally {
      setBusy(false);
    }
  };

  const onCreateMap = async (input: { tenantId: string; name: string; file: File | null }): Promise<boolean> => {
    try {
      const { mapId } = await createMapApi(input.tenantId, input.name);
      if (input.file) {
        try {
          const bytes = await input.file.arrayBuffer();
          const isJson = input.file.name.endsWith('.json') || input.file.name.endsWith('.geojson');
          await uploadRaw(
            `/api/v1/tenants/${input.tenantId}/maps/${mapId}/imports?filename=${encodeURIComponent(input.file.name)}&as=master`,
            bytes,
            isJson ? 'application/geo+json' : 'application/gpx+xml',
          );
          setMessage(t('map.new.createdWithData'));
        } catch {
          setMessage(t('map.new.uploadFailed'));
        }
      } else {
        setMessage(`${t('map.new.created')}: ${input.name}`);
      }
      await loadMaps(mapId);
      setTab('map');
      setReviewId(null);
      setFeatureInfo(null);
      return true;
    } catch {
      setMessage(t('map.new.failed'));
      return false;
    }
  };

  const onSaveSettings = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !settings) return;
    setSettingsSaving(true);
    try {
      const result = await putMapSettings(selected.tenantId, selected.id, {
        identityStrategy: settings.identityStrategy,
        moveToleranceM: settings.moveToleranceM,
        geometryEqToleranceM: settings.geometryEqToleranceM,
        requiredApprovals: settings.requiredApprovals,
      });
      setMessage(
        result.invalidated > 0
          ? t('settings.savedReapproval', { count: result.invalidated })
          : result.recomputed > 0
            ? t('settings.savedRecomputed', { count: result.recomputed })
            : t('settings.saved'),
      );
      await Promise.all([
        loadSettings(selected),
        loadProposals(selected),
        ...(reviewId ? [loadReview(selected, reviewId)] : []),
      ]);
    } catch {
      setMessage(t('settings.saveFailed'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const onRenameMap = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !mapName.trim()) return;
    setSettingsSaving(true);
    try {
      const result = await renameMap(selected.tenantId, selected.id, mapName.trim());
      setMaps((current) =>
        current.map((map) => (map.id === selected.id ? { ...map, name: result.name } : map)),
      );
      setSelected((current) => (current && current.id === selected.id ? { ...current, name: result.name } : current));
      setMessage(t('settings.renamed'));
    } catch {
      setMessage(t('settings.renameFailed'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const onReindex = async () => {
    if (!selected) return;
    setSettingsSaving(true);
    try {
      const result = await reindexMap(selected.tenantId, selected.id);
      setMessage(`${t('settings.reindexed')} (${result.features})`);
      await loadFeatures(selected);
    } finally {
      setSettingsSaving(false);
    }
  };

  const onVerify = async () => {
    if (!selected) return;
    try {
      const result = await verifyChain(selected.tenantId, selected.id);
      setMessage(
        result.ok
          ? `${t('history.verified')} (${result.checked})`
          : `${t('history.verifyBroken')} #${result.brokenAt}`,
      );
    } catch {
      setMessage(t('history.verifyBroken'));
    }
  };

  const reviewBase = selected && reviewId ? `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/proposals/${reviewId}` : '';

  const stopEditing = () => {
    resetEditor();
  };

  const startEditing = async (proposalId?: string) => {
    if (!selected) return;
    setFeatureInfo(null);
    setReviewId(null);
    let features: Feature[] = [];
    try {
      // Always edit the FULL master (not the filtered view) so a filter can never
      // turn into proposed deletions.
      features = proposalId
        ? (await getProposedFeatures(selected.tenantId, selected.id, proposalId)).features
        : (
            await getJson<FeatureCollection>(
              `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/features`,
            )
          ).features;
    } catch {
      features = data?.features ?? [];
    }
    setEditBase({ type: 'FeatureCollection', features });
    setEditProposalId(proposalId ?? null);
    setDrafts({});
    setDeletedIds([]);
    setSelectedFeatureId(null);
    setSelectedVertex(null);
    setArmed(null);
    setDrawCoords([]);
    setEditing(true);
  };

  const updateSelected = (patch: {
    name?: string;
    type?: string;
    description?: string;
    geometry?: { type: string; coordinates: unknown };
  }) => {
    if (!selectedFeatureId) return;
    const base = baseFeatures.find((f) => f.id === selectedFeatureId);
    const current = drafts[selectedFeatureId] ?? base;
    if (!current) return;
    const next: Feature = {
      ...current,
      geometry: patch.geometry ?? current.geometry,
      properties: { ...current.properties },
    };
    if (patch.name !== undefined) next.properties.name = patch.name;
    if (patch.type !== undefined) next.properties.type = patch.type;
    if (patch.description !== undefined) next.properties.description = patch.description;
    const id = selectedFeatureId;
    setDrafts((d) => ({ ...d, [id]: next }));
  };

  const deleteSelected = () => {
    const id = selectedFeatureId;
    if (!id) return;
    const inBase = baseFeatures.some((f) => f.id === id);
    setDrafts((d) => {
      const copy = { ...d };
      delete copy[id];
      return copy;
    });
    if (inBase) setDeletedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
    setSelectedFeatureId(null);
    setSelectedVertex(null);
  };

  const onMapClick = (latlng: { lat: number; lng: number }) => {
    if (!editing) return;
    const coordinate: [number, number] = [latlng.lng, latlng.lat];
    if (armed === 'add') {
      const id = newFeatureId();
      const feature: Feature = {
        type: 'Feature',
        id,
        geometry: { type: 'Point', coordinates: coordinate },
        properties: { name: '', type: '', _waystone: newWaystoneMeta() },
      };
      setDrafts((d) => ({ ...d, [id]: feature }));
      setSelectedFeatureId(id);
      setArmed(null);
      return;
    }
    if (armed === 'move' && selectedFeatureId) {
      updateSelected({ geometry: { type: 'Point', coordinates: coordinate } });
      setArmed(null);
      return;
    }
    if (isDrawing) {
      setDrawCoords((coords) => [...coords, coordinate]);
      return;
    }
    if (armed === 'addVertex' && canEditVertices && selectedFeature) {
      updateSelected({ geometry: geometryWithVertices(selectedFeature, [...shapeVertices, coordinate]) });
    }
  };

  const moveVertex = (index: number, lngLat: { lat: number; lng: number }) => {
    if (!canEditVertices || !selectedFeature) return;
    const next = shapeVertices.slice();
    next[index] = [lngLat.lng, lngLat.lat];
    updateSelected({ geometry: geometryWithVertices(selectedFeature, next) });
  };

  const deleteVertex = () => {
    if (!canEditVertices || !selectedFeature || selectedVertex === null) return;
    const next = shapeVertices.filter((_, index) => index !== selectedVertex);
    if (next.length < minimumVertices(selectedFeature)) return;
    updateSelected({ geometry: geometryWithVertices(selectedFeature, next) });
    setSelectedVertex(null);
  };

  const finishDrawing = () => {
    const isArea = armed === 'drawArea';
    const minimum = isArea ? 3 : 2;
    if (drawCoords.length < minimum) {
      setDrawCoords([]);
      setArmed(null);
      return;
    }
    const id = newFeatureId();
    const geometry = isArea
      ? { type: 'Polygon', coordinates: [[...drawCoords, drawCoords[0]!]] }
      : { type: 'LineString', coordinates: drawCoords };
    const feature: Feature = {
      type: 'Feature',
      id,
      geometry,
      properties: { name: '', type: '', _waystone: newWaystoneMeta(isArea ? 'area' : 'track') },
    };
    setDrafts((d) => ({ ...d, [id]: feature }));
    setSelectedFeatureId(id);
    setSelectedVertex(null);
    setDrawCoords([]);
    setArmed(null);
  };

  const saveEdits = async () => {
    if (!selected) return;
    const operations: EditOperation[] = [
      ...Object.values(drafts).map((feature) => ({ op: 'upsert' as const, feature })),
      ...deletedIds.map((id) => ({ op: 'delete' as const, id })),
    ];
    if (operations.length === 0) {
      setMessage(t('edit.noChanges'));
      return;
    }
    setSavingEdit(true);
    try {
      const result = editProposalId
        ? await updateEditProposal(selected.tenantId, selected.id, editProposalId, operations)
        : await createEditProposal(selected.tenantId, selected.id, operations);
      if (result.noChanges) {
        setMessage(t('edit.noChanges'));
        return;
      }
      setMessage(t('edit.saved'));
      stopEditing();
      await Promise.all([loadFeatures(selected), loadProposals(selected)]);
      setTab('proposals');
      if (result.proposalId) setReviewId(result.proposalId);
    } catch (err) {
      setMessage(String(err));
    } finally {
      setSavingEdit(false);
    }
  };

  const decide = async (itemKeys: string[] | null, decision: Decision) => {
    if (!reviewBase) return;
    setBusy(true);
    try {
      await postJson(`${reviewBase}/decisions`, { itemKeys, decision });
      await loadReview(selected, reviewId);
    } finally {
      setBusy(false);
    }
  };

  const vote = async (v: 'approve' | 'reject') => {
    if (!reviewBase) return;
    setBusy(true);
    try {
      await postJson(`${reviewBase}/approve`, { vote: v });
      await loadReview(selected, reviewId);
      await loadProposals(selected);
    } finally {
      setBusy(false);
    }
  };

  const merge = async () => {
    if (!reviewBase) return;
    setBusy(true);
    try {
      const result = (await postJson(`${reviewBase}/merge`, {})) as {
        revisionId: number;
        revisionNumber: number;
        applied: number;
      };
      setMessage(`${t('review.merge')}: #${result.revisionNumber} (+${result.applied})`);
      await Promise.all([
        loadReview(selected, reviewId),
        loadProposals(selected),
        loadFeatures(selected),
        loadRevisions(selected),
        loadNotifications(selected),
      ]);
    } catch (err) {
      setMessage(String(err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!reviewBase) return;
    setBusy(true);
    try {
      await postJson(`${reviewBase}/withdraw`, {});
      setMessage(t('status.aborted'));
      await loadProposals(selected);
      await loadReview(selected, reviewId);
    } finally {
      setBusy(false);
    }
  };

  const addComment = async (event: FormEvent<HTMLFormElement>, itemKey: string | null) => {
    event.preventDefault();
    const text = itemKey ? threadText : commentText;
    if (!reviewBase || !text.trim()) return;
    setBusy(true);
    try {
      await postJson(`${reviewBase}/comments`, { text: text.trim(), itemKey });
      if (itemKey) setThreadText('');
      else setCommentText('');
      await loadReview(selected, reviewId);
    } finally {
      setBusy(false);
    }
  };

  const revert = async (revisionId: number) => {
    if (!selected) return;
    setBusy(true);
    try {
      await postJson(`/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/revisions/${revisionId}/revert`, {});
      setMessage(t('history.reverted'));
      await Promise.all([loadFeatures(selected), loadRevisions(selected), loadProposals(selected)]);
    } catch (err) {
      setMessage(String(err));
    } finally {
      setBusy(false);
    }
  };

  const markAllRead = async () => {
    if (!selected) return;
    await postJson(`/api/v1/tenants/${selected.tenantId}/notifications/read`, {});
    await loadNotifications(selected);
  };

  const markRead = async (id: string) => {
    if (!selected) return;
    await postJson(`/api/v1/tenants/${selected.tenantId}/notifications/read`, { ids: [id] });
    await loadNotifications(selected);
  };

  const onDeleteMap = async () => {
    if (!selected) return;
    setSettingsSaving(true);
    try {
      await deleteMapApi(selected.tenantId, selected.id);
      setMessage(t('map.delete.done'));
      setDeleteConfirm(false);
      setReviewId(null);
      setFeatureInfo(null);
      setTab('map');
      resetEditor();
      await loadMaps();
    } catch {
      setMessage(t('map.delete.failed'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const openFeature = async (feature: { id: string; name?: string }) => {
    if (!selected || !feature.id) return;
    const full = data?.features.find((f) => f.id === feature.id);
    setFeatureInfo({
      id: feature.id,
      name: feature.name,
      properties: full?.properties ?? {},
      geometry: full?.geometry,
    });
    try {
      setFeatureHistory((await getFeatureHistory(selected.tenantId, selected.id, feature.id)).history);
    } catch {
      setFeatureHistory([]);
    }
  };

  const focusedChange = useMemo(
    () => (detailKey ? changes.find((c) => c.itemKey === detailKey) : undefined),
    [detailKey, changes],
  );
  const candidateFeatures = useMemo(
    () => candidatesFor(focusedChange, data, settings),
    [focusedChange, data, settings],
  );
  const focusGeometry = useMemo(
    () => focusGeometryFor(focusedChange, candidateFeatures),
    [focusedChange, candidateFeatures],
  );

  if (!me) {
    return (
      <div className="login">
        <div className="login-header">
          <div className="login-brand">
            <Logo size={44} />
            <h1>{t('app.title')}</h1>
          </div>
          <button
            type="button"
            className="button login-lang"
            onClick={() => setLang(lang === 'sv' ? 'en' : 'sv')}
            aria-label={lang === 'sv' ? 'Switch to English' : 'Byt till svenska'}
          >
            {lang === 'sv' ? 'EN' : 'SV'}
          </button>
        </div>
        <p className="login-description">{t('login.description')}</p>
        <h2>{t('login.heading')}</h2>
        <form className="ui-form" onSubmit={onTokenLogin}>
          <Field
            label={t('login.token')}
            hint={t('login.tokenHint', { minutes: authOptions?.loginTokenTtlMinutes ?? 30 })}
          >
            <input
              name="token"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
          </Field>
          <ActionRow>
            <button type="submit" className="button primary">
              {t('login.tokenSubmit')}
            </button>
          </ActionRow>
        </form>
        {authOptions?.mailEnabled && (
          <>
            <h2>{t('login.requestHeading')}</h2>
            <form className="ui-form" onSubmit={onRequestLink}>
              <Field label={t('login.email')} hint={t('login.requestHint')}>
                <input name="email" type="email" autoComplete="email" required />
              </Field>
              <ActionRow>
                <button type="submit" className="button">
                  {t('login.requestSubmit')}
                </button>
              </ActionRow>
            </form>
          </>
        )}
        {authOptions && !authOptions.mailEnabled && (
          <p className="muted">{t('login.contactAdmin')}</p>
        )}
        {authOptions?.devLogin && (
          <>
            <h2>{t('login.devHeading')}</h2>
            <form className="ui-form" onSubmit={onDevLogin}>
              <Field label={t('login.email')} hint={t('login.devHint')}>
                <input name="email" type="email" defaultValue="admin@example.com" required />
              </Field>
              <ActionRow>
                <button type="submit" className="button">
                  {t('login.devSubmit')}
                </button>
              </ActionRow>
            </form>
          </>
        )}
        {message && <p className="login-message">{message}</p>}
        <p className="login-version">Waystone v{APP_VERSION}</p>
      </div>
    );
  }

  const review = Boolean(reviewId && selected);
  const grouped: Array<{ action: ChangeItem['action']; label: string }> = [
    { action: 'add', label: t('review.add') },
    { action: 'delete', label: t('review.delete') },
    { action: 'update', label: t('review.update') },
  ];
  const unread = notifications.filter((n) => !n.readAt).length;
  const actionable = detail && detail.status !== 'done' && detail.status !== 'aborted';
  const proposalComments = comments.filter((c) => c.itemKey === null || c.orphaned);
  const nonMoveDeletes = changes.filter((c) => c.action === 'delete' && !c.possibleMoveOf).length;
  const visibleWarnings: Array<Warning | string> = (detail?.summary?.warnings ?? []).flatMap(
    (warning): Array<Warning | string> => {
      if (typeof warning === 'string') return [warning];
      if (warning.code !== 'bulk-removal') return [warning];
      if (nonMoveDeletes === 0) return [];
      const originalCount = Number(warning.params.count ?? nonMoveDeletes);
      const originalShare = Number(warning.params.share ?? 0);
      const share =
        originalCount > 0 ? Math.round((originalShare * nonMoveDeletes) / originalCount) : originalShare;
      return [{ ...warning, params: { ...warning.params, count: nonMoveDeletes, share } }];
    },
  );
  const activeProposals = proposals.filter((p) => p.status !== 'done' && p.status !== 'aborted');
  const closedProposals = proposals.filter((p) => p.status === 'done' || p.status === 'aborted');

  const proposalRow = (p: Proposal) => {
    const terminal = p.status === 'done' || p.status === 'aborted';
    return (
      <li key={p.id}>
        <button
          className={`link proposal-item${terminal ? ' terminal' : ''}`}
          onClick={() => {
            setReviewId(p.id);
            setDetailKey(null);
          }}
        >
          <span className="proposal-title">{p.title}</span>
          <span className={`badge ${p.status}`}>{t(`status.${p.status}`)}</span>
          <span className="muted">
            +{p.summary?.counts.add ?? 0} / ~{p.summary?.counts.update ?? 0} / -
            {p.summary?.counts.delete ?? 0}
          </span>
        </button>
      </li>
    );
  };

  const tabDefs: Array<[Tab, string]> = [
    ['map', t('tabs.map')],
    ['proposals', t('tabs.proposals')],
    ['history', t('tabs.history')],
    ...(canConfigure ? ([['settings', t('tabs.settings')]] as Array<[Tab, string]>) : []),
  ];

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-left">
          <span className="brand">
            <Logo size={22} />
            <span>{t('app.title')}</span>
            <span className="brand-version">v{APP_VERSION}</span>
          </span>
          {maps.length > 0 && (
            <label className="map-picker">
              <span className="sr-only">{t('map.choose')}</span>
              <select
                value={selected?.id ?? ''}
                onChange={(e) => {
                  resetEditor();
                  setAdminOpen(false);
                  setTeamOpen(false);
                  setSelected(maps.find((m) => m.id === e.target.value) ?? null);
                  setReviewId(null);
                  setFeatureInfo(null);
                  setTab('map');
                }}
              >
                {maps.map((m) => (
                  <option key={m.id} value={m.id}>
                    {tenantLabel(m.tenantName, m.tenantId)} — {m.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!adminOpen && !teamOpen && adminTenants.length > 0 && (
            <button className="new-map-btn" onClick={() => setShowNewMap(true)}>
              + {t('actions.newMap')}
            </button>
          )}
        </div>
        <div className="topbar-right">
          {selected && !adminOpen && !teamOpen && (
            <NotificationsMenu
              notifications={notifications}
              unread={unread}
              open={showNotifications}
              onToggle={() => setShowNotifications((v) => !v)}
              onClose={() => setShowNotifications(false)}
              onMarkRead={() => void markAllRead()}
              onRead={(id) => void markRead(id)}
              t={t}
            />
          )}
          {adminTenants.length > 0 && (
            <button
              className={teamOpen ? 'active' : ''}
              onClick={() => {
                setTeamOpen((value) => !value);
                setAdminOpen(false);
                setShowNotifications(false);
              }}
            >
              {t('tabs.team')}
            </button>
          )}
          {me.user.is_platform_admin && (
            <button
              className={adminOpen ? 'active' : ''}
              onClick={() => {
                setAdminOpen((value) => !value);
                setTeamOpen(false);
                setShowNotifications(false);
              }}
            >
              {t('admin.title')}
            </button>
          )}
          <button onClick={() => setLang(lang === 'sv' ? 'en' : 'sv')}>{lang === 'sv' ? 'EN' : 'SV'}</button>
          <button onClick={() => void onLogout()}>{t('actions.logout')}</button>
        </div>
      </header>

      {message && (
        <div className="toast" role="status">
          <span>{message}</span>
          <button className="toast-close" onClick={() => setMessage('')} aria-label={t('actions.dismiss')}>
            ✕
          </button>
        </div>
      )}

      {adminOpen ? (
        <AdminPage
          t={t}
          onClose={() => {
            setAdminOpen(false);
            void (async () => {
              if (await loadMe()) await loadMaps();
            })();
          }}
        />
      ) : teamOpen ? (
        <TeamPage
          memberships={me.memberships}
          t={t}
          onClose={() => {
            setTeamOpen(false);
            void (async () => {
              if (await loadMe()) await loadMaps();
            })();
          }}
          onChanged={async () => {
            if (await loadMe()) await loadMaps();
          }}
        />
      ) : (
      <div className="body">
        {selected && editing && (
          <aside className="panel">
            <div className="panel-head">
              <strong>{t('edit.title')}</strong>
              <button className="link" onClick={stopEditing}>
                {t('edit.cancel')}
              </button>
            </div>
            <p className="muted">{t('edit.hint')}</p>
            <Section title={t('edit.tools')}>
              <ActionRow>
                <button
                  className={`button${armed === 'add' ? ' primary' : ''}`}
                  onClick={() => setArmed(armed === 'add' ? null : 'add')}
                >
                  {t('edit.addPoint')}
                </button>
                <button
                  className={`button${armed === 'drawLine' ? ' primary' : ''}`}
                  onClick={() => {
                    setDrawCoords([]);
                    setSelectedVertex(null);
                    setArmed(armed === 'drawLine' ? null : 'drawLine');
                  }}
                >
                  {t('edit.addLine')}
                </button>
                <button
                  className={`button${armed === 'drawArea' ? ' primary' : ''}`}
                  onClick={() => {
                    setDrawCoords([]);
                    setSelectedVertex(null);
                    setArmed(armed === 'drawArea' ? null : 'drawArea');
                  }}
                >
                  {t('edit.addArea')}
                </button>
                <button
                  className={`button${armed === 'move' ? ' primary' : ''}`}
                  disabled={!selectedFeature || selectedFeature.geometry.type !== 'Point'}
                  onClick={() => setArmed(armed === 'move' ? null : 'move')}
                >
                  {t('edit.move')}
                </button>
                <button className="button" disabled={!selectedFeatureId} onClick={deleteSelected}>
                  {t('edit.delete')}
                </button>
              </ActionRow>
              {canEditVertices && (
                <ActionRow>
                  <button
                    className={`button${armed === 'addVertex' ? ' primary' : ''}`}
                    onClick={() => setArmed(armed === 'addVertex' ? null : 'addVertex')}
                  >
                    {t('edit.addVertex')}
                  </button>
                  <button className="button" disabled={selectedVertex === null} onClick={deleteVertex}>
                    {t('edit.deleteVertex')}
                  </button>
                </ActionRow>
              )}
              {isDrawing && (
                <ActionRow>
                  <button
                    className="button primary"
                    disabled={drawCoords.length < (armed === 'drawArea' ? 3 : 2)}
                    onClick={finishDrawing}
                  >
                    {t('edit.finishLine')}
                  </button>
                  <span className="muted">
                    {drawCoords.length} {t('change.vertices')}
                  </span>
                </ActionRow>
              )}
              {armed && (
                <p className="warning">
                  {armed === 'add'
                    ? t('edit.clickToAdd')
                    : armed === 'move'
                      ? t('edit.clickToMove')
                      : armed === 'drawArea'
                        ? t('edit.clickToDrawArea')
                        : armed === 'drawLine'
                          ? t('edit.clickToDraw')
                          : t('edit.clickToAddVertex')}
                </p>
              )}
            </Section>
            <Section title={t('edit.object')}>
              {selectedFeature ? (
                <form className="ui-form" onSubmit={(e) => e.preventDefault()}>
                  <FieldGrid>
                    <Field label={t('edit.name')}>
                      <input
                        value={String(selectedFeature.properties.name ?? '')}
                        onChange={(e) => updateSelected({ name: e.target.value })}
                      />
                    </Field>
                    <TypeField
                      value={String(selectedFeature.properties.type ?? '')}
                      onChange={(type) => updateSelected({ type })}
                      options={typeCatalog}
                      lang={lang}
                      t={t}
                    />
                  </FieldGrid>
                  <Field label={t('edit.description')}>
                    <textarea
                      rows={2}
                      value={String(selectedFeature.properties.description ?? '')}
                      onChange={(e) => updateSelected({ description: e.target.value })}
                    />
                  </Field>
                </form>
              ) : (
                <p className="muted">{t('edit.select')}</p>
              )}
            </Section>
            <Section title={t('edit.changesTitle')}>
              <ul>
                {Object.values(drafts).map((f) => (
                  <li key={f.id}>
                    <button className="link change-name" onClick={() => setSelectedFeatureId(f.id)}>
                      {String(f.properties.name || t('edit.untitled'))}
                    </button>
                  </li>
                ))}
                {deletedIds.map((id) => (
                  <li key={id}>
                    <span className="change-name">
                      {t('edit.deleted')}: {String(baseFeatures.find((f) => f.id === id)?.properties.name ?? id)}
                    </span>
                  </li>
                ))}
              </ul>
              <ActionRow>
                <button className="button primary" disabled={savingEdit} onClick={() => void saveEdits()}>
                  {t('edit.save')}
                </button>
              </ActionRow>
              <p className="muted">
                {t('edit.changes')}: {Object.keys(drafts).length + deletedIds.length}
              </p>
            </Section>
          </aside>
        )}

        {selected && !editing && (
          <aside className="panel">
            {featureInfo ? (
              <>
                <div className="panel-head">
                  <strong>{t('feature.history.title')}</strong>
                  <button className="link" onClick={() => setFeatureInfo(null)}>
                    {t('review.back')}
                  </button>
                </div>
                <p className="change-name">{featureInfo.name ?? featureInfo.id}</p>
                {featureInfo.properties && (
                  <table className="diff feature-attrs">
                    <tbody>
                      {[...propertyValues(featureInfo.properties).entries()]
                        .filter(([label]) => label !== 'name')
                        .map(([label, value]) => (
                          <tr key={label}>
                            <td className="diff-label">{label}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                      <tr>
                        <td className="diff-label">{t('change.coordinates')}</td>
                        <td>{formatGeometry(featureInfo.geometry, t)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                {featureHistory.length > 0 && (
                  <Section title={t('history.title')}>
                    <ul>
                      {featureHistory.map((h) => (
                        <li key={`${h.revisionId}-${h.change}`}>
                          <span
                            className={`badge ${h.change === 'removed' ? 'rejected' : h.change === 'added' ? 'approved' : 'open'}`}
                          >
                            {t(`feature.${h.change}`)}
                          </span>
                          <span className="rev">#{h.revisionNumber}</span>
                          <em>{h.authorEmail ?? ''}</em>
                          <em>{new Date(h.createdAt).toLocaleDateString()}</em>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}
                {featureHistory.length === 0 && <p>{t('feature.empty')}</p>}
              </>
            ) : (
              <>
                <div className="map-head">
                  <strong>{selected.name}</strong>
                  <span className="muted">
                    {tenantLabel(selected.tenantName, selected.tenantId)} · {t(`role.${selected.role}`)}
                  </span>
                </div>
                <nav className="tabs">
                  {tabDefs.map(([key, label]) => (
                    <button
                      key={key}
                      className={tab === key ? 'active' : ''}
                      onClick={() => {
                        setTab(key);
                        setReviewId(null);
                        setFeatureInfo(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </nav>

                <div className="tab-body">
                  {tab === 'map' && (
                    <>
                      <Section title={t('map.view')}>
                        <Field label={t('layer.label')}>
                          <select value={layer} onChange={(e) => setLayer(e.target.value as LayerKey)}>
                            <option value="topo">{t('layer.topo')}</option>
                            <option value="osm">{t('layer.osm')}</option>
                            <option value="satellite">{t('layer.satellite')}</option>
                          </select>
                        </Field>
                      </Section>
                      <Section
                        title={t('filter.label')}
                        actions={
                          <IconButton
                            label={t('filter.clear')}
                            disabled={!type && !kind && !q}
                            onClick={() => {
                              setType('');
                              setKind('');
                              setQ('');
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        }
                      >
                        <FieldGrid>
                          <Field label={t('filter.kind')}>
                            <select value={kind} onChange={(e) => setKind(e.target.value)}>
                              <option value="">{t('filter.all')}</option>
                              <option value="waypoint">{t('kind.waypoint')}</option>
                              <option value="track">{t('kind.track')}</option>
                              <option value="route">{t('kind.route')}</option>
                              <option value="area">{t('kind.area')}</option>
                            </select>
                          </Field>
                          <Field label={t('filter.type')}>
                            <select value={type} onChange={(e) => setType(e.target.value)}>
                              <option value="">{t('filter.all')}</option>
                              {allTypes.map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label={t('filter.search')} className="wide">
                            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} />
                          </Field>
                        </FieldGrid>
                      </Section>
                      <Section title={t('map.actions')}>
                        <ActionRow>
                          <input
                            id="import-file"
                            type="file"
                            accept=".gpx,.geojson,.json"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) void onImport(file);
                              e.currentTarget.value = '';
                            }}
                          />
                          <button
                            className="button primary"
                            onClick={() => document.getElementById('import-file')?.click()}
                            disabled={busy}
                          >
                            {t('actions.import')}
                          </button>
                          {canContribute && (
                            <button className="button" onClick={() => void startEditing()}>
                              {t('actions.edit')}
                            </button>
                          )}
                        </ActionRow>
                      </Section>
                      <Section title={t('actions.export')}>
                        <ActionRow>
                          <button
                            className="button"
                            type="button"
                            onClick={() => {
                              if (!selected) return;
                              window.location.href = `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/export.gpx`;
                            }}
                          >
                            {t('actions.exportGpx')}
                          </button>
                          <button
                            className="button"
                            type="button"
                            onClick={() => {
                              if (!selected) return;
                              window.location.href = `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/artifact`;
                            }}
                          >
                            {t('actions.exportGeoJson')}
                          </button>
                        </ActionRow>
                        <details className="export-more">
                          <summary>{t('export.more')}</summary>
                          <ActionRow>
                            <button
                              className="button"
                              type="button"
                              onClick={() => {
                                if (!selected) return;
                                window.location.href = `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/export.gpx?version=1.0`;
                              }}
                            >
                              {t('export.gpx10')}
                            </button>
                            <button
                              className="button"
                              type="button"
                              onClick={() => {
                                if (!selected) return;
                                window.location.href = `/api/v1/tenants/${selected.tenantId}/maps/${selected.id}/export.zip`;
                              }}
                            >
                              {t('export.zip')}
                            </button>
                          </ActionRow>
                        </details>
                      </Section>
                    </>
                  )}

                  {tab === 'proposals' && !review && (
                    <>
                      <Section title={t('proposals.open')}>
                        {proposals.length === 0 && <p>{t('proposals.empty')}</p>}
                        <ul className="proposal-list">{activeProposals.map(proposalRow)}</ul>
                      </Section>
                      {closedProposals.length > 0 && (
                        <Section title={t('proposals.closed')}>
                          <ul className="proposal-list closed">{closedProposals.map(proposalRow)}</ul>
                        </Section>
                      )}
                    </>
                  )}

                  {tab === 'proposals' && review && (
                    <>
                      <div className="panel-head">
                        <strong>{t('review.proposalActions')}</strong>
                        <button className="link" onClick={() => setReviewId(null)}>
                          {t('proposals.back')}
                        </button>
                      </div>

                      {detail && (
                        <div className="status-row">
                          <span className={`badge ${detail.status}`}>{t(`status.${detail.status}`)}</span>
                          <span className="quorum">
                            {t('review.quorum')}: {detail.approvals.counted}/{detail.approvals.required}
                            {detail.approvals.quorumMet ? ' ✓' : ''}
                          </span>
                          {detail.authorId === me?.user.id && role === 'contributor' && (
                            <span className="muted">{t('review.ownVoteNoCount')}</span>
                          )}
                          {detail.status === 'done' && detail.mergedRevisionId != null && (
                            <span className="muted">
                              {t('review.mergedAs')}{' '}
                              {detail.mergedRevisionNumber ?? detail.mergedRevisionId}
                            </span>
                          )}
                        </div>
                      )}

                      {detail && !actionable && <p className="muted">{t('review.closed')}</p>}

                      {canApprove && actionable && (
                        <ActionRow>
                          <button className="button" disabled={busy} onClick={() => void vote('approve')}>
                            {t('review.approve')}
                          </button>
                          <button className="button" disabled={busy} onClick={() => void vote('reject')}>
                            {t('review.rejectProposal')}
                          </button>
                          {(detail?.authorId === me?.user.id || role === 'tenant_admin') && (
                            <button className="button" disabled={busy} onClick={() => void withdraw()}>
                              {t('review.withdraw')}
                            </button>
                          )}
                          {canMerge && (
                            <button
                              className="button primary"
                              disabled={
                                busy ||
                                (role !== 'tenant_admin' && !detail?.approvals.quorumMet) ||
                                (detail?.counts.pending ?? 0) > 0 ||
                                detail?.counts.accepted === 0 ||
                                detail?.status === 'rejected'
                              }
                              onClick={() => void merge()}
                            >
                              {t('review.merge')}
                            </button>
                          )}
                        </ActionRow>
                      )}

                      {!canApprove && <p className="muted">{t('review.noPermission')}</p>}

                      <hr className="proposal-divider" />

                      <Section title={`${t('review.comments')} (${proposalComments.length})`}>
                        {proposalComments.length === 0 && <p className="muted">{t('review.comment.none')}</p>}
                        <ul className="comments">
                          {proposalComments.map((c) => (
                            <li key={c.id}>
                              <strong>{c.authorEmail}</strong> {c.body}
                              {c.orphaned && <em> · {t('review.comment.orphaned')}</em>}
                            </li>
                          ))}
                        </ul>
                        {canComment && actionable && (
                          <form className="comment-form" onSubmit={(e) => void addComment(e, null)}>
                            <input
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              placeholder={t('review.comment.placeholder')}
                            />
                            <button
                              type="submit"
                              className="button"
                              disabled={busy || !commentText.trim()}
                            >
                              {t('review.comment.add')}
                            </button>
                          </form>
                        )}
                      </Section>

                      <hr className="proposal-divider" />

                      {visibleWarnings.map((warning, index) => {
                        const severity = warningSeverity(warning);
                        return (
                          <div
                            key={`${index}-${typeof warning === 'string' ? warning : warning.code}`}
                            className={`warning ${severity}`}
                          >
                            <span className="warning-icon" aria-hidden="true">
                              {severity === 'action' ? '⛔' : severity === 'attention' ? '⚠' : 'ℹ'}
                            </span>
                            <span>{warningText(warning, t)}</span>
                          </div>
                        );
                      })}

                      <Section title={t('review.changeActions')}>
                        <ActionRow>
                          {actionable &&
                            canContribute &&
                            (role === 'tenant_admin' || detail?.authorId === me?.user.id) && (
                              <button
                                className="button"
                                disabled={busy || !reviewId}
                                onClick={() => reviewId && void startEditing(reviewId)}
                              >
                                {t('edit.continue')}
                              </button>
                            )}
                          {canApprove && actionable && (
                            <>
                              <button className="button" disabled={busy} onClick={() => void decide(null, 'accepted')}>
                                {t('review.acceptAll')}
                              </button>
                              <button className="button" disabled={busy} onClick={() => void decide(null, 'rejected')}>
                                {t('review.rejectAll')}
                              </button>
                            </>
                          )}
                        </ActionRow>
                        {actionable && detail && detail.counts.pending > 0 && (
                          <div className="warning attention">{t('review.pendingBlocksMerge')}</div>
                        )}
                      </Section>

                      {sortedChanges.length === 0 && <p>{t('review.empty')}</p>}
                      {grouped.map(({ action, label }) => {
                        const items = sortedChanges.filter((c) => c.action === action);
                        if (items.length === 0) return null;
                        return (
                          <Section key={action} title={`${label} (${items.length})`}>
                            <ul>
                              {items.map((c) => {
                                const thread = commentsByItem.get(c.itemKey) ?? [];
                                const partner = c.possibleMoveOf ? movePartners.get(c.possibleMoveOf) : undefined;
                                const showRemovalGroup = Boolean(c.removalGroup && !c.possibleMoveOf);
                                const before = c.before ?? partner?.del?.before ?? null;
                                const after = c.after ?? partner?.add?.after ?? null;
                                const diff = changeDiff(before, after, t);
                                const missingName = isUnnamed(c);
                                return (
                                  <li
                                    key={c.itemKey}
                                    id={`change-${c.itemKey}`}
                                    className={`change ${c.decision}${detailKey === c.itemKey ? ' open' : ''}`}
                                  >
                                    <div className="change-head">
                                      <span className={`dot ${action}`} />
                                      <button
                                        className={`link change-name${missingName ? ' unnamed' : ''}`}
                                        onClick={() => setDetailKey(detailKey === c.itemKey ? null : c.itemKey)}
                                      >
                                        {nameOf(c, t('review.unnamed'))}
                                      </button>
                                      {missingName && (
                                        <span className="chip missing-name">{t('chip.missing-name')}</span>
                                      )}
                                      {c.possibleMoveOf && <span className="chip move">{t('review.move')}</span>}
                                      {c.ambiguity && c.ambiguity !== 'unnamed-track' && (
                                        <span className={`chip ambiguity ${c.ambiguity}`}>
                                          {t(`chip.${c.ambiguity}`)}
                                        </span>
                                      )}
                                      {canApprove && actionable && (
                                        <span className="decide">
                                          <button
                                            className={c.decision === 'accepted' ? 'on accept' : ''}
                                            title={t('review.accept')}
                                            disabled={busy}
                                            onClick={() =>
                                              void decide(
                                                [c.itemKey],
                                                c.decision === 'accepted' ? 'pending' : 'accepted',
                                              )
                                            }
                                          >
                                            ✓
                                          </button>
                                          <button
                                            className={c.decision === 'rejected' ? 'on reject' : ''}
                                            title={t('review.rejectItem')}
                                            disabled={busy}
                                            onClick={() =>
                                              void decide(
                                                [c.itemKey],
                                                c.decision === 'rejected' ? 'pending' : 'rejected',
                                              )
                                            }
                                          >
                                            ✗
                                          </button>
                                        </span>
                                      )}
                                      <button
                                        className="link thread-toggle"
                                        title={t('review.comments')}
                                        onClick={() => setThreadKey(threadKey === c.itemKey ? null : c.itemKey)}
                                      >
                                        💬{thread.length > 0 ? ` ${thread.length}` : ''}
                                      </button>
                                      <button
                                        className="link detail-toggle"
                                        title={t('change.details')}
                                        onClick={() => setDetailKey(detailKey === c.itemKey ? null : c.itemKey)}
                                      >
                                        {detailKey === c.itemKey ? '▾' : '▸'}
                                      </button>
                                    </div>
                                    {detailKey === c.itemKey && (
                                      <div className="change-detail">
                                        <div className="muted detail-reason">
                                          {c.action === 'add'
                                            ? `${t('change.noMatch')} ${(settings?.identityStrategy ?? []).join(' + ') || t('change.noStrategy')}`
                                            : c.action === 'delete'
                                              ? t('change.absent')
                                              : `${t('change.matchedOn')} ${(settings?.identityStrategy ?? []).join(' + ') || t('change.noStrategy')}`}
                                        </div>
                                        {(c.possibleMoveOf || c.driftGroup || showRemovalGroup || c.ambiguity) && (
                                          <ul className="change-notices">
                                            {c.possibleMoveOf && (
                                              <li>
                                                <strong>{t('review.move')}</strong> {t('notice.move')}
                                              </li>
                                            )}
                                            {c.driftGroup && (
                                              <li>
                                                <strong>{t('review.systematic')}</strong> {t('notice.systematic')}
                                              </li>
                                            )}
                                            {showRemovalGroup && (
                                              <li>
                                                <strong>{t('review.removalGroup')}</strong> {t('notice.removalGroup')}
                                              </li>
                                            )}
                                            {c.ambiguity && (
                                              <li>
                                                <strong>{t(`chip.${c.ambiguity}`)}</strong>{' '}
                                                {t(`notice.${c.ambiguity}`)}
                                                {c.ambiguity === 'two-candidates' &&
                                                  candidateFeatures.length > 0 && (
                                                    <> {t('notice.two-candidates.map')}</>
                                                  )}
                                              </li>
                                            )}
                                          </ul>
                                        )}
                                        {c.possibleMoveOf && partner?.add && partner?.del && canApprove && actionable && (
                                          <div className="toolbar detail-move-actions">
                                            <button
                                              disabled={busy}
                                              onClick={() => void decide([partner.add!.itemKey, partner.del!.itemKey], 'accepted')}
                                            >
                                              {t('review.acceptMove')}
                                            </button>
                                            <button
                                              disabled={busy}
                                              onClick={() => void decide([partner.add!.itemKey, partner.del!.itemKey], 'rejected')}
                                            >
                                              {t('review.rejectMove')}
                                            </button>
                                          </div>
                                        )}
                                        <table className="diff">
                                          <thead>
                                            <tr>
                                              <th />
                                              <th>{t('change.from')}</th>
                                              <th>{t('change.to')}</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(before && after
                                              ? diff.filter((row) => row.changed)
                                              : diff.filter((row) => row.from !== '—' || row.to !== '—')
                                            ).map((row) => (
                                              <tr key={row.label} className={row.changed ? 'changed' : ''}>
                                                <td className="diff-label">{row.label}</td>
                                                <td>{row.from}</td>
                                                <td>{row.to}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                        {before && after && diff.every((row) => !row.changed) && (
                                          <p className="muted">{t('change.noFieldChanges')}</p>
                                        )}
                                        {(() => {
                                          const from = before ? firstCoord(before.geometry) : null;
                                          const to = after ? firstCoord(after.geometry) : null;
                                          if (!from || !to) return null;
                                          const metres = distanceMeters(from, to);
                                          if (metres < 0.5) return null;
                                          return (
                                            <div className="muted detail-move">
                                              {t('change.moved')} {Math.round(metres)} m
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                    {threadKey === c.itemKey && (
                                      <div className="thread">
                                        {thread.length === 0 && <p className="muted">{t('review.comment.none')}</p>}
                                        <ul className="comments">
                                          {thread.map((cm) => (
                                            <li key={cm.id}>
                                              <strong>{cm.authorEmail}</strong> {cm.body}
                                            </li>
                                          ))}
                                        </ul>
                                        {canComment && actionable && (
                                          <form className="comment-form" onSubmit={(e) => void addComment(e, c.itemKey)}>
                                            <input
                                              value={threadText}
                                              onChange={(e) => setThreadText(e.target.value)}
                                              placeholder={t('review.comment.placeholder')}
                                            />
                                            <button
                                              type="submit"
                                              className="button"
                                              disabled={busy || !threadText.trim()}
                                            >
                                              {t('review.comment.add')}
                                            </button>
                                          </form>
                                        )}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </Section>
                        );
                      })}
                    </>
                  )}

                  {tab === 'history' && (
                    <Section
                      title={t('history.title')}
                      actions={
                        canApprove ? (
                          <button className="button" disabled={busy} onClick={() => void onVerify()}>
                            {t('history.verify')}
                          </button>
                        ) : undefined
                      }
                    >
                      {revisions.length === 0 && <p>{t('history.empty')}</p>}
                      <ul>
                        {revisions.map((r) => (
                          <li key={r.id}>
                            <span className="rev">#{r.number}</span>
                            <span className="change-name">{revisionText(r)}</span>
                            <em>{r.authorEmail ?? ''}</em>
                            {role === 'tenant_admin' && r.id !== selected.currentRevisionId && (
                              <button
                                className="button"
                                disabled={busy}
                                onClick={() => void revert(r.id)}
                              >
                                {t('history.revert')}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}

                  {tab === 'settings' && canConfigure && (
                    <>
                      <Section title={t('settings.mapSection')}>
                        <form className="ui-form" onSubmit={onRenameMap}>
                          <Field label={t('settings.mapName')}>
                            <input value={mapName} onChange={(e) => setMapName(e.target.value)} />
                          </Field>
                          <ActionRow>
                            <button
                              type="submit"
                              className="button"
                              disabled={settingsSaving || !mapName.trim()}
                            >
                              {t('settings.rename')}
                            </button>
                          </ActionRow>
                        </form>
                      </Section>

                      <Section
                        title={t('settings.matching')}
                        description={t('settings.matching.hint')}
                      >
                        {!settings && <p className="muted">{t('map.loading')}</p>}
                        {settings && (
                          <form className="ui-form" onSubmit={onSaveSettings}>
                            <Field label={t('settings.identity')} hint={t('settings.identity.hint')}>
                              <select
                                value={(settings.identityStrategy ?? []).join(',')}
                                onChange={(e) =>
                                  setSettings({
                                    ...settings,
                                    identityStrategy: e.target.value.split(','),
                                  })
                                }
                              >
                                <option value="name">{t('settings.identity.name')}</option>
                                <option value="name,type">{t('settings.identity.nameType')}</option>
                                <option value="name,type,description">
                                  {t('settings.identity.nameTypeDesc')}
                                </option>
                                <option value="id">{t('settings.identity.id')}</option>
                              </select>
                            </Field>
                            <FieldGrid>
                              <Field label={t('settings.moveTolerance')}>
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={settings.moveToleranceM}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      moveToleranceM: Number(e.target.value),
                                    })
                                  }
                                />
                              </Field>
                              <Field label={t('settings.geometryTolerance')}>
                                <input
                                  type="number"
                                  min={0}
                                  step={0.5}
                                  value={settings.geometryEqToleranceM}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      geometryEqToleranceM: Number(e.target.value),
                                    })
                                  }
                                />
                              </Field>
                            </FieldGrid>
                            <Field label={t('settings.requiredApprovals')}>
                              <input
                                type="number"
                                min={1}
                                step={1}
                                value={settings.requiredApprovals}
                                onChange={(e) =>
                                  setSettings({
                                    ...settings,
                                    requiredApprovals: Number(e.target.value),
                                  })
                                }
                              />
                            </Field>
                            <ActionRow>
                              <button type="submit" className="button primary" disabled={settingsSaving}>
                                {t('settings.save')}
                              </button>
                            </ActionRow>
                          </form>
                        )}
                      </Section>

                      {settings && (
                        <Section
                          title={t('settings.advanced')}
                          description={t('settings.reindex.hint')}
                        >
                          <ActionRow>
                            <button
                              type="button"
                              className="button"
                              disabled={settingsSaving}
                              onClick={() => void onReindex()}
                            >
                              {t('settings.reindex')}
                            </button>
                          </ActionRow>
                        </Section>
                      )}

                      <Section title={t('map.delete.title')} description={t('map.delete.hint')}>
                        <ActionRow>
                          {deleteConfirm ? (
                            <>
                              <button
                                className="button danger"
                                disabled={settingsSaving}
                                onClick={() => void onDeleteMap()}
                              >
                                {t('map.delete.confirm')}
                              </button>
                              <button type="button" className="button" onClick={() => setDeleteConfirm(false)}>
                                {t('map.new.cancel')}
                              </button>
                            </>
                          ) : (
                            <button className="button danger" onClick={() => setDeleteConfirm(true)}>
                              {t('map.delete.button')}
                            </button>
                          )}
                        </ActionRow>
                      </Section>
                    </>
                  )}
                </div>
              </>
            )}
          </aside>
        )}

        <div className="map-area">
          {selected ? (
            <MapView
              data={editing ? editData : data}
              changes={review && !editing ? changes : undefined}
              layer={layer}
              missingNameLabel={t('map.missingName')}
              onFeatureClick={(f) => {
                if (editing) {
                  setSelectedFeatureId(f.id);
                  setSelectedVertex(null);
                } else if (!review || featureInfo) {
                  // While reviewing, clicking an unchanged point must not hijack
                  // the review panel (but it may change an already-open feature).
                  void openFeature(f);
                }
              }}
              editable={editing}
              onMapClick={onMapClick}
              selectedFeatureId={selectedFeatureId}
              fitKey={selected.id}
              vertices={editing ? vertices : undefined}
              selectedVertex={editing ? selectedVertex : null}
              onVertexDrag={editing && canEditVertices ? moveVertex : undefined}
              onVertexClick={(index) => setSelectedVertex(index)}
              previewLine={editing ? previewLine : undefined}
              highlightKey={review ? detailKey : null}
              focusKey={review ? detailKey : null}
              focusGeometry={focusGeometry}
              candidates={candidateFeatures}
              candidateLabel={t('review.candidate')}
              onChangeClick={(key) => {
                setDetailKey(key);
                setThreadKey(null);
              }}
            />
          ) : (
            <div className="empty-map">
              <p>{t('map.none')}</p>
              <p className="muted">{t('map.none.hint')}</p>
              {adminTenants.length > 0 && (
                <button onClick={() => setShowNewMap(true)}>+ {t('actions.newMap')}</button>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {showNewMap && adminTenants.length > 0 && (
        <NewMapDialog
          tenants={adminTenants}
          initialTenantId={
            selected && adminTenants.some((a) => a.id === selected.tenantId) ? selected.tenantId : undefined
          }
          t={t}
          onClose={() => setShowNewMap(false)}
          onCreate={onCreateMap}
        />
      )}
    </div>
  );
}
